import { type MappingStatusFilter } from '@/components/Grid/CellRenderers'
import { useTenantStore } from '@/stores/tenantStore'
import { fetchClient } from '@api/typedClient'
import { GridPayload } from '@api/types'
import {
  ColDef,
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from 'ag-grid-community'
import { useCallback, useMemo, useRef, useState } from 'react'

interface UsePaginatedGridDatasourceValue<TData = unknown> {
  datasource: IServerSideDatasource
  columnDefs: ColDef<TData>[]
  refresh: () => void
  totalRows: number
}

interface UsePaginatedGridDatasourceParams {
  endpoint: string
  searchTerm?: string
  mappingType?: string
  onlyMapped?: boolean
  mappingStatusFilters?: MappingStatusFilter[]
  sourceIds?: string[]
  recordOwner?: string
}

/**
 * Returns a IServerSideDatasource that can be used in a server-side paginated grid. Use this when calling /grid endpoints since it can parse the data best
 * If this seems to be causing an infinite loop rendering cycle, check that the data IDs are getting set. Duplicate row IDs cause re-renders for some reason.
 */
export function usePaginatedGridDatasource<TData = unknown>(
  props: UsePaginatedGridDatasourceParams,
): UsePaginatedGridDatasourceValue<TData> {
  const { selectedTenant } = useTenantStore()
  const [gridColumnDefs, setGridColumnDefs] = useState<ColDef<TData>[]>([])
  const columnDefsJsonRef = useRef<string>('[]')
  const [refreshVersion, setRefreshVersion] = useState<number>(0)
  const [totalRows, setTotalRows] = useState<number>(0)

  const updateColumnDefs = useCallback((newDefs: ColDef<TData>[]) => {
    const json = JSON.stringify(newDefs)
    if (json !== columnDefsJsonRef.current) {
      columnDefsJsonRef.current = json
      setGridColumnDefs(newDefs)
    }
  }, [])

  type FilterKey =
    | 'limit'
    | 'skip'
    | 'type'
    | 'search_term'
    | 'status'
    | 'source_ids'
    | 'sort_field'
    | 'sort_dir'
    | 'record_owner'

  const datasource = useMemo(() => {
    console.trace('[datasource] useMemo recreated')
    return {
      getRows: async (params: IServerSideGetRowsParams<TData>) => {
        console.trace('[datasource] getRows called')
        if (selectedTenant?.name) {
          const queryParams: Partial<
            Record<FilterKey, string | number | boolean | null>
          > = {}
          const { startRow, endRow, sortModel } = params.request
          queryParams.limit = endRow && startRow ? endRow - startRow : 1000
          queryParams.skip = startRow ? startRow : 0
          if (props.mappingType) queryParams.type = props.mappingType
          if (props.searchTerm && props.searchTerm.length > 0)
            queryParams.search_term = props.searchTerm

          if (
            props.mappingStatusFilters &&
            props.mappingStatusFilters.length > 0
          ) {
            queryParams['status'] = props.mappingStatusFilters.join(',')
          }

          if (props.sourceIds && props.sourceIds.length > 0)
            queryParams['source_ids'] = props.sourceIds.join(',')

          const filterModel = params.request.filterModel as Record<
            string,
            { filterType: string; values: string[] }
          > | null
          const sourceIdFilter = filterModel?.['source_id']
          if (sourceIdFilter) {
            if (!sourceIdFilter.values?.length) {
              // Filter is active but nothing selected — return empty
              params.success({ rowData: [], rowCount: 0 })
              return
            }
            const filterValues = sourceIdFilter.values.join(',')
            const existing = queryParams['source_ids']
            queryParams['source_ids'] = existing
              ? `${existing},${filterValues}`
              : filterValues
          }

          if (props.recordOwner) queryParams.record_owner = props.recordOwner

          const firstSort = sortModel?.[0]
          if (firstSort) {
            queryParams.sort_field = firstSort.colId
            queryParams.sort_dir = firstSort.sort ?? 'asc'
          }
          const { data: responseData, error } = await fetchClient.GET(
            // @ts-expect-error TODO: Dynamic grid endpoints not in OpenAPI spec
            props.endpoint,
            {
              params: {
                query: queryParams,
              },
            },
          )

          if (error) {
            console.error('Failed to fetch grid data:', error)
            params.fail()
            return
          }

          const data = responseData as unknown as GridPayload<TData>

          updateColumnDefs(data.column_defs)
          setTotalRows(data.num_results)

          params.success({
            rowData: data.rows,
            rowCount: data.num_results,
          })
        } else {
          params.success({ rowData: [], rowCount: -1 })
        }
      },
    }
  }, [
    selectedTenant?.name,
    props.endpoint,
    props.searchTerm,
    props.mappingType,
    props.onlyMapped,
    props.mappingStatusFilters,
    props.sourceIds,
    props.recordOwner,
    refreshVersion,
  ])

  const refresh = () => {
    setRefreshVersion((v) => v + 1)
  }

  return {
    datasource,
    columnDefs: gridColumnDefs,
    refresh,
    totalRows,
  }
}
